import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Grid, Group, Checkbox, NativeSelect, Button, createStyles } from "@mantine/core";
import PdfLabel from "./PdfLabel";
import { StapelUseCount } from "./StapelUseCount";
import intl from "react-intl-universal";

const useStyles = createStyles((theme) => ({
selectallrow: {
  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
  borderRadius: theme.radius.sm,
  paddingTop: 3,
  paddingBottom: 3,
  marginBottom: 15,
},
}));

/* =====================
   TYPES
===================== */

export type DocumentSignMode = {
  resourceId: string;
  signMode: "visible" | "invisible";
};

export type UserStapel = {
  stapelId?: number;          // vom Backend (optional)
  localStapelId?: string;     // optional UI-ID
  participantId: string;
  documentsSignModes: DocumentSignMode[];
};

type Props = {
  participantId: string;
  participantName: string;
  editdialog: boolean;
  initialStapel?: UserStapel | null;
  onSave: (stapel: UserStapel) => void;
  onClose: () => void;
};

/* =====================
   COMPONENT
===================== */

const SetupStapelSignDialog = ({
  participantId,
  participantName,
  editdialog,
  initialStapel,
  onSave,
  onClose,
   
}: Props) => {
  /* =====================
     REDUX (READ ONLY)
  ===================== */

  const meetingDocs =
    useSelector((state: any) => state.user.meetingdocs) || [];

  const filteredDocs = useMemo(
    () => meetingDocs.filter((d: any) => d.isToBeSigned && d.isToBeRead),
    [meetingDocs]
  );

  const isPDF = (fileName: string) => fileName.endsWith(".pdf");
  const { classes } = useStyles();


  /* =====================
     LOCAL STATE (EDIT)
  ===================== */

  const [documents, setDocuments] = useState<DocumentSignMode[]>([]);
  const [isnew, setIsNew] = useState<boolean>(true);

  /* =====================
     INIT (EDIT ODER NEW)
  ===================== */



  useEffect(() => {
    if (editdialog && initialStapel) {
      // ✅ EDIT
      setDocuments(initialStapel.documentsSignModes ?? []);
    } else {
      // ✅ NEW
      setIsNew(false);
      setDocuments([]);
    }
  }, [editdialog, initialStapel]);

    //console.log("documents", documents);

  /* =====================
     HANDLER
  ===================== */
    const { getUsageCount } = StapelUseCount(
          participantId,
          initialStapel?.stapelId,
          initialStapel?.localStapelId,
          { includeCurrentStapel: isnew }
        );

  const allChecked =
    filteredDocs.length > 0 &&
    filteredDocs.every((doc: any) =>
      documents.some((d) => d.resourceId === doc.resourceId)
    );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setDocuments(
        filteredDocs.map((doc: any) => ({
          resourceId: doc.resourceId,
          signMode: "visible" as const,
        }))
      );
    } else {
      setDocuments([]);
    }
  };

  const handleCheckboxChange = (
    checked: boolean,
    resourceId: string
  ) => {
    setDocuments((prev) => {
      if (checked) {
        if (prev.some((d) => d.resourceId === resourceId)) {
          return prev;
        }
        return [...prev, { resourceId, signMode: "visible" }];
      }
      return prev.filter((d) => d.resourceId !== resourceId);
    });
  };

  const handleSelectChange = (
    value: "visible" | "invisible",
    resourceId: string
  ) => {
    setDocuments((prev) =>
      prev.map((d) =>
        d.resourceId === resourceId ? { ...d, signMode: value } : d
      )
    );
  };

  const handleSave = () => {
    onSave({
      stapelId: initialStapel?.stapelId,
      localStapelId: initialStapel?.localStapelId,
      participantId,
      documentsSignModes: documents,
    });
  }; 

  /* =====================
     RENDER
  ===================== */

  //console.log("render", {"Count filteredDocs": filteredDocs.length });

  return (
    <>
      <p>
        {intl.get("SETUPSTAPELSIGNDIALOG_INFO")}{" "}
        <strong>{participantName}</strong>
      </p>
      {filteredDocs.length >= 1 && 
        <>
      <Grid className={classes.selectallrow} >
        <Grid.Col span={11}>{intl.get('SETUP_SELECTALL')}</Grid.Col>
        <Grid.Col span={1}>
          <Checkbox
            checked={allChecked}
            size="lg"
            color="green"
            onChange={(e) => handleSelectAll(e.currentTarget.checked)}
            styles={{ input: { backgroundColor: "#ededed", borderColor: "green" } }}
          />
        </Grid.Col>
        </Grid>
        </>
        }
        
      <Grid>  
        {filteredDocs.map((doc: any) => {

          const found = documents.find(
            (d) => d.resourceId === doc.resourceId
          );

          const isChecked = Boolean(found);
          const signMode = found?.signMode ?? "visible";

          const usageCount = getUsageCount(doc.resourceId);

          return (
            <React.Fragment key={doc.resourceId}>
              <Grid.Col span={11}>
                <Group spacing="xs">
                  {isPDF(doc.name) && (
                    <PdfLabel
                      isPDFA1b={doc.isPDFA1b}
                      signatureCount={doc.signatureCount}
                    />
                  )}
                  {doc.name} {usageCount}
                </Group>
              </Grid.Col>

              <Grid.Col span={1}>
                <Checkbox
                  checked={isChecked}
                  size="lg"
                  color="green"
                  onChange={(e) =>
                    handleCheckboxChange(
                      e.currentTarget.checked,
                      doc.resourceId
                    )
                  }
                   styles={{
                input: {
                  backgroundColor: "#ededed",
                  borderColor: "green",
                },
  }}
                />
              </Grid.Col>

      
            </React.Fragment>
          );
        })}
      </Grid>

      <hr />

      <Group position="right">
        <Button variant="default" onClick={onClose}>
          {intl.get("SETUPSTAPELSIGNDIALOG_BUTTON_CANCEL")}
        </Button>
        <Button onClick={handleSave}>
          {intl.get("SETUPSTAPELSIGNDIALOG_BUTTON_AGREE")}
        </Button>
      </Group>
    </>
  );
};

export default SetupStapelSignDialog;

// eslint-disable-next-line no-lone-blocks
{/* 
<NativeSelect
                  size="sm"
                  disabled={!isChecked}
                  value={signMode}
                  onChange={(e) =>
                    handleSelectChange(
                      e.currentTarget.value as "visible" | "invisible",
                      doc.resourceId
                    )
                  }
                  data={[
                    { value: "visible", label: "Sichtbar" },
                    { value: "invisible", label: "Unsichtbar" },
                  ]}
                  style={{ width: 120 }}
                />
  */}





